import {
  Input,
  Output,
  EventEmitter,
  Injector,
  OnInit,
  AfterContentInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Renderer2,
  OnDestroy,
  Directive,
} from "@angular/core";
import {
  ValidationErrors,
  ControlValueAccessor,
  NgControl,
  UntypedFormControl,
  NgModel,
  Validator,
} from "@angular/forms";
import { Subscription } from "rxjs";
import { GenerateUuid } from "src/app/shared/utils";

@Directive()
export abstract class FieldBaseComponent
  implements
    ControlValueAccessor,
    Validator,
    OnInit,
    OnDestroy,
    AfterContentInit,
    AfterViewInit
{
  protected subscriptions: Subscription[] = [];
  protected _required: boolean = false;

  @ViewChild("input", { static: false, read: NgControl }) input: NgModel;
  @ViewChild("input") inputRef: ElementRef;

  @Input() id: string = `field-${GenerateUuid()}`;
  @Input() displayLabel: string;
  @Input() readOnly: boolean = false;
  @Input() errorMsg: string | null;
  @Input() errors: any = null;
  @Input() cls: string | null;
  @Input() clsInput: string | null = '';
  @Input() clsLabel: string | null = "col-form-label";

  @Input() set required(value: boolean) {
    this._required = value;
  };

  @Input() set tabIndex(value: number) {
    setTimeout(
      function () {
        this.setTabIndex(value);
      }.bind(this),
      1000
    );
  }

  // tslint:disable-next-line: no-output-on-prefix
  @Output() onChangeValue = new EventEmitter();
  @Output() blur = new EventEmitter();

  fieldName: string;
  value: any;
  disabled: boolean = false;
  customCss: {};

  protected renderer: Renderer2;
  protected elRef: ElementRef;
  protected ngControl: NgControl;
  protected formControl: UntypedFormControl;
  // protected errors: ValidationErrors = {};

  constructor(protected injector: Injector) {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
    this.elRef = this.injector.get(ElementRef);
    this.renderer = this.injector.get(Renderer2);

    this.renderer.setStyle(this.elRef.nativeElement, "position", "relative");
  }

  ngAfterContentInit(): void {
    if (this.ngControl) {
      this.fieldName = this.ngControl.name + "";
      this.formControl = this.ngControl.control as UntypedFormControl;
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(
      function () {
        this.setValidators();
      }.bind(this),
      100
    );

    if (this.formControl) {
      this.subscriptions.push(
        this.formControl.statusChanges.subscribe((status) => {
          this.setValidators();
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private setValidators(): void {
    if (!!this.input && !!this.formControl) {
      let validator = null;
      if (!!this.formControl.validator) {
        validator = this.formControl.validator({} as UntypedFormControl);
      }

      if (!this._required) {
        this._required = validator ? validator.required : false;
      }

      if (validator) {
        this.input.control.setValidators(this.formControl.validator);
      } else {
        this.input.control.clearValidators();
      }
      this.input.control.updateValueAndValidity();
    }
  }

  private setTabIndex(value: number): void {
    if (!!this.renderer && !!this.inputRef) {
      this.renderer.setAttribute(
        this.inputRef.nativeElement,
        "tabIndex",
        value + ""
      );
    }
  }

  _onChange = (_: any) => {};
  _onTouched = () => {};

  onChange(value: any): void {
    this._onChange(value);
  }

  onTouched(): void {
    this.input.control.markAsTouched();
    this.customCss = this.displayFieldCss();
  }

  onBlur(event: Event): void {
    this.onTouched();
    this.blur.emit(event);
  }

  isFieldInvalid(): boolean {
    return (
      !!this.formControl && !this.formControl.valid && this.formControl.touched
    );
  }

  isRequired(): boolean {
    return this._required;
  }

  private clearErrors(): void {
    this.errors = [];
    this.errorMsg = null;
    if (this.formControl) {
      this.formControl.setErrors(null, { emitEvent: true });
      this.formControl.updateValueAndValidity();
    }
    if (this.input) {
      this.input.control.setErrors(null, { emitEvent: true });
      this.input.control.updateValueAndValidity();
    }
  }

  getFieldError(): ValidationErrors {
    // return (!!this.formControl && this.formControl.errors) || this.errors || {};
    // return (!!this.input &&  !!this.input.control && this.input.control.errors) || this.errors || {};
    return this.errors || [];
  }

  get touched(): boolean {
    return this.ngControl ? this.ngControl.touched : false;
  }

  displayFieldCss() {
    const invalid = this.isFieldInvalid();
    return {
      "has-error": invalid,
      "has-feedback": invalid,
    };
  }

  public registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  writeValue(value: any): void {
    this.value = value;
  }

  valueChange(value: any): void {
    try {
      this.clearErrors();
      this.onChange(value);
      this.onChangeValue.emit(value);
    } catch (e) {}
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(ctl: UntypedFormControl): ValidationErrors | null {
    const errors: any[] = [];

    if (!this.formControl) {
      this.formControl = ctl;
    }

    // Caso a variável não esteja instanciada ou ela não seja um array
    // define como array para não ter problemas com o tipo
    if (!this.errors || (!!this.errors && !(this.errors instanceof Array))) {
      this.errors = [];
    }

    if (this.formControl && this.formControl.errors) {
      this.errors.push(this.formControl.errors);
    }

    this.errors &&
      this.errors.forEach((error) => {
        if (error.type == 'required' && !ctl.value) {
          errors.push({ required: true, message: error.message });
        } else if (error.type == 'invalid' && ctl.value) {
          errors.push({ invalid: true, message: error.message });
        }

      });

    return errors.length ? { error: errors } : null;
  }

  focus(): void {
    if (this.inputRef && this.inputRef.nativeElement) {
      setTimeout(() => this.inputRef.nativeElement.focus(), 10);
    }
  }

  select(): void {
    if (this.inputRef && this.inputRef.nativeElement) {
      setTimeout(() => this.inputRef.nativeElement.select(), 10);
    }
  }
}
